import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext, useState } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import EmailTextField from 'shared/lib/components/controls/textFields/emailTextField';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

export default observer(function RestoreLinkForm({
  onFinishClick,
}: {
  onFinishClick: () => void;
}) {
  const { t } = useTranslation();

  const {
    mobxStore: { authStore },
  } = useContext(MobxContext);
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object({
    email: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .email(t('errors.validationSchema.email')),
  });

  const [error, setError] = useState<null | 'emailNotExists'>(null);
  const [formikSubmited, setFormikSubmited] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async ({ email }) => {
      try {
        setIsLoading(true);
        await authStore.sendRestoreLink({ email });
        setIsLoading(false);
      } catch (e) {
        const err = e as AxiosError;

        if (err.response?.status === 401) {
          setError('emailNotExists');
        }

        console.error(e);
        setIsLoading(false);
      }
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  const isPasswordLinkSend = authStore.isPasswordLinkSend;

  return (
    <>
      {isPasswordLinkSend ? (
        <Typography variant="f14" sx={{ textAlign: 'center', fontWeight: 300 }}>
          На&nbsp;&laquo;
          <span style={{ fontWeight: 700 }}>{formik.values.email}</span>&raquo;
          отправлено письмо со&nbsp;ссылкой на&nbsp;восстановление пароля.{' '}
          <span style={{ fontWeight: 700 }}>
            Ссылка активна в&nbsp;течении 7&nbsp;дней
          </span>
        </Typography>
      ) : (
        <Typography variant="f14" sx={{ textAlign: 'center', fontWeight: 300 }}>
          Чтобы восстановить пароль, введите Email, который вы&nbsp;использовали
          при регистрации учетной записи. Мы&nbsp;отправим вам{' '}
          <span style={{ fontWeight: 700 }}>ссылку для сброса пароля.</span>
        </Typography>
      )}
      {isPasswordLinkSend ? null : (
        <form
          id="inviteForm"
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(sp.s60),
          }}
          onSubmit={formik.handleSubmit}
        >
          <EmailTextField
            label={t('login.email')}
            id="email"
            name="email"
            placeholder={t('login.emailPlaceholder')}
            value={formik.values.email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setError(null);
              handleOnChange(e);
            }}
            onBlur={formik.handleBlur}
            error={isFormikFieldError('email') || !!error}
            helperText={
              getFormikFieldErrorText('email') ||
              (error && t(`errors.${error}`))
            }
            autoFocus={true}
            labelSxProps={{
              paddingLeft: '12px',
              color: theme.palette.custom.customDark,
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.palette.custom.customDark,
              },
            }}
          ></EmailTextField>
        </form>
      )}

      <PrimaryButton
        sx={{
          width: 1,
          borderRadius: '16px',
        }}
        form="inviteForm"
        type="submit"
        loading={isLoading}
        onClick={(e) => {
          if (isPasswordLinkSend) {
            e.stopPropagation();
            e.preventDefault();
            onFinishClick();
            return;
          }
          setFormikSubmited(true);
        }}
      >
        <Typography variant="f16">
          {isPasswordLinkSend ? t('common.finish') : t('login.sendLink')}
        </Typography>
      </PrimaryButton>
    </>
  );
});
